import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { InterStoreTransferJobs } from './InterStoreTransferJobs';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Stack,
  Chip,
} from '@mui/material';
import { UploadIcon, TrendingUpIcon, FileIcon, DeleteIcon } from 'lucide-react';
import { useAuthHeader } from '../../../hooks/useAuth';
import type { SimpleAccount } from '@/lib/types';
import { getAppConfig } from '../../../lib/config';
import {
  analyzeInterStoreTransfer,
  type InterStoreTransferAnalysisRequest,
  type InterStoreTransferAnalysisResponse,
} from '../../../lib/api/inter-store-transfer-analysis';

interface InterStoreTransferAnalysisProps {
  account: SimpleAccount;
}

export function InterStoreTransferAnalysis({ account }: InterStoreTransferAnalysisProps) {
  const { getAuthHeader } = useAuthHeader();

  // 入力項目の状態
  const [transferOrderFile, setTransferOrderFile] = useState<File | null>(null);
  const [inventoryDate, setInventoryDate] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [transferCost, setTransferCost] = useState<number | undefined>(undefined);
  const [executionRateStartDate, setExecutionRateStartDate] = useState<string>('');
  const [executionRateEndDate, setExecutionRateEndDate] = useState<string>('');

  // UI状態
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setTransferOrderFile(file);
  };

  const handleRemoveFile = () => {
    setTransferOrderFile(null);
  };

  const handleAnalyze = async () => {
    setError(null);
    setSuccess(null);

    // バリデーション
    if (!transferOrderFile) {
      setError('移動指示書CSVファイルを選択してください');
      return;
    }

    if (!inventoryDate) {
      setError('在庫計上日を入力してください');
      return;
    }

    if (!startDate || !endDate) {
      setError('集計期間（開始日・終了日）を入力してください');
      return;
    }

    if (startDate >= endDate) {
      setError('開始日は終了日より前の日付を入力してください');
      return;
    }

    if (!executionRateStartDate || !executionRateEndDate) {
      setError('実行率集計期間（開始日・終了日）を入力してください');
      return;
    }

    if (executionRateStartDate >= executionRateEndDate) {
      setError('実行率集計開始日は終了日より前の日付を入力してください');
      return;
    }

    if (transferCost !== undefined && transferCost < 0) {
      setError('移動費は0以上の値を入力してください');
      return;
    }

    try {
      setIsAnalyzing(true);

      const request: InterStoreTransferAnalysisRequest = {
        inventoryDate,
        startDate,
        endDate,
        executionRateStartDate,
        executionRateEndDate,
        transferCost,
        transferOrderFile,
      };

      const authHeaders = getAuthHeader();
      const result = await analyzeInterStoreTransfer(account.id, request, authHeaders);

      // 結果に基づいてメッセージを設定
      let successMessage = result.message;
      if (result.execution_duration_ms) {
        const durationSeconds = (result.execution_duration_ms / 1000).toFixed(1);
        successMessage += ` (実行時間: ${durationSeconds}秒)`;
      }

      if (result.job_status === 'completed') {
        setSuccess(`店間移動効果検証の分析が完了しました。${successMessage}`);
      } else if (result.job_status === 'running') {
        setSuccess(
          `店間移動効果検証の分析を開始しました。処理はバックグラウンドで実行されます。${successMessage}`,
        );
      } else if (result.job_status === 'pending') {
        setSuccess(`店間移動効果検証の分析ジョブを登録しました。${successMessage}`);
      } else {
        setSuccess(successMessage);
      }
    } catch (error) {
      console.error('Analysis failed:', error);
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('分析に失敗しました。入力内容を確認してください。');
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  const FileUploadCard = ({
    title,
    required,
    file,
    onFileChange,
    onRemoveFile,
    description,
  }: {
    title: string;
    required: boolean;
    file: File | null;
    onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onRemoveFile: () => void;
    description: string;
  }) => (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1">{title}</Typography>
            {required ? (
              <Chip label="必須" size="small" variant="outlined" color="error" />
            ) : (
              <Chip label="任意" size="small" variant="outlined" color="default" />
            )}
          </Box>
          {file && (
            <Button
              size="small"
              color="error"
              onClick={onRemoveFile}
              startIcon={<DeleteIcon size={16} />}
            >
              削除
            </Button>
          )}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>
        {file ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 2,
              bgcolor: 'grey.50',
              borderRadius: 1,
            }}
          >
            <FileIcon size={16} style={{ marginRight: 8 }} />
            <Typography variant="body2">{file.name}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              ({(file.size / 1024).toFixed(1)} KB)
            </Typography>
          </Box>
        ) : (
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadIcon size={16} />}
            fullWidth
          >
            CSVファイルを選択
            <input type="file" accept=".csv" hidden onChange={onFileChange} />
          </Button>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <TrendingUpIcon size={24} style={{ marginRight: 8 }} />
        店間移動効果検証
      </Typography>

      <Tabs defaultValue="analysis" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="analysis">分析実行</TabsTrigger>
          <TabsTrigger value="logs">実行ログ</TabsTrigger>
        </TabsList>

        <TabsContent value="analysis" className="space-y-6 mt-6">
          <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
                {success}
              </Alert>
            )}

            <Stack spacing={3}>
              {/* ファイルアップロード */}
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 3 }}>
                    ファイルアップロード
                  </Typography>

                  <FileUploadCard
                    title="移動指示書"
                    required
                    file={transferOrderFile}
                    onFileChange={handleFileUpload}
                    onRemoveFile={handleRemoveFile}
                    description="SKUカスタマーID、SKU名、出荷先倉庫カスタマーID、出荷先倉庫名、出荷元倉庫カスタマーID、出荷元倉庫名、移動数を含むCSVファイル"
                  />
                </CardContent>
              </Card>

              {/* 日付設定 */}
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 3 }}>
                    日付設定
                  </Typography>

                  <Stack spacing={3}>
                    {/* 在庫計上日 */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Typography variant="subtitle1">在庫計上日</Typography>
                        <Chip label="必須" size="small" variant="outlined" color="error" />
                      </Box>
                      <TextField
                        label="在庫計上日"
                        type="date"
                        value={inventoryDate}
                        onChange={(e) => setInventoryDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      />
                    </Box>

                    {/* 集計期間 */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Typography variant="subtitle1">集計期間</Typography>
                        <Chip label="必須" size="small" variant="outlined" color="error" />
                      </Box>
                      <Stack direction="row" spacing={2}>
                        <TextField
                          label="開始日"
                          type="date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                        />
                        <TextField
                          label="終了日"
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                        />
                      </Stack>
                    </Box>

                    {/* 実行率集計期間 */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Typography variant="subtitle1">実行率集計期間</Typography>
                        <Chip label="必須" size="small" variant="outlined" color="error" />
                      </Box>
                      <Stack direction="row" spacing={2}>
                        <TextField
                          label="開始日"
                          type="date"
                          value={executionRateStartDate}
                          onChange={(e) => setExecutionRateStartDate(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                        />
                        <TextField
                          label="終了日"
                          type="date"
                          value={executionRateEndDate}
                          onChange={(e) => setExecutionRateEndDate(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                        />
                      </Stack>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, display: 'block' }}
                      >
                        実行率分析で使用する期間を入力してください
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>

              {/* 移動費設定 */}
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 3 }}>
                    追加情報
                  </Typography>

                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Typography variant="subtitle1">移動費</Typography>
                      <Chip label="任意" size="small" variant="outlined" color="default" />
                    </Box>
                    <TextField
                      label="移動費"
                      type="number"
                      value={transferCost ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setTransferCost(value === '' ? undefined : parseInt(value) || 0);
                      }}
                      inputProps={{ min: 0, step: 1 }}
                      fullWidth
                      placeholder="移動にかかる費用（正の整数）"
                      helperText="移動にかかる費用を入力してください（任意項目）"
                    />
                  </Box>
                </CardContent>
              </Card>

              {/* 実行ボタン */}
              <Button
                variant="contained"
                size="large"
                onClick={handleAnalyze}
                disabled={isAnalyzing}
                startIcon={
                  isAnalyzing ? <CircularProgress size={16} /> : <TrendingUpIcon size={16} />
                }
                fullWidth
                sx={{ mt: 3 }}
              >
                {isAnalyzing ? '分析実行中...' : '分析実行'}
              </Button>
            </Stack>
          </Box>
        </TabsContent>

        <TabsContent value="logs" className="mt-6">
          <InterStoreTransferJobs account={account} />
        </TabsContent>
      </Tabs>
    </Box>
  );
}
